import type { JSX } from 'solid-js';
import { t } from '../i18n';
import { OnboardingAdvanceButton } from './OnboardingAdvanceButton';
import { buttonClass } from './ui/Button';

// The `.maintainer.yml` step's content. Presenter — the container owns the
// clipboard write and decides whether a successful copy advances the funnel.
//
// THE PULL REQUEST IS THE PATH (arc 101 slice 05 step 6). There were two of them
// and neither knew about the other: this block used to lead with "paste this
// prompt into your agent and commit the file", while the repo-onboarding pass —
// minted on first enable, one task per enabled repo — proposes that exact file on
// `dz/onboarding` and waits for a human to merge it. So a maintainer who did what
// the platform built for them got no credit for it, and one who did what this card
// said wrote a file the pass then reported as `skipped_present`. The PR leads now;
// the hand-written file is the fallback it always was, kept because a repo whose
// pass is still waiting on a box, and a maintainer who would rather write their
// own, both still need it. `ensureFile` never clobbers, so the two cannot collide.
//
// The fallback file pins `pr.auto_merge: false` and `release.manager: none` on
// purpose, and the copy says why. They are NOT decoration: the SCHEMA defaults are
// `true` and `release-please` (`packages/policy/src/schema.ts`), so the two-line
// `version: 1` file the public quickstart shows turns on auto-merge and public
// GitHub Releases — which is how tags got cut on three repos that had adopted
// nothing (`packages/policy/src/defaults.ts`, #1784). A starting point handed to
// someone who has not read the schema yet has to be the hands-off one. The pass's
// own proposal (`renderMaintainerPolicy`, `apps/runner/src/modes/setup/base/maintainer.ts`)
// pins the same two lines for the same reason.
//
// Rendered at `repos_selected` AND at `yml_requested`: the prompt used to vanish
// the instant it was copied, and there is no backward edge out of
// `yml_requested`, so the text the step is ABOUT became unreachable.
export type OnboardingYmlBlockProps = {
  readonly busy: boolean;
  readonly onCopy: (text: string) => void;
};

export function OnboardingYmlBlock(props: OnboardingYmlBlockProps): JSX.Element {
  return (
    <>
      <section class="onboarding__yml">
        <h3 class="onboarding__yml-heading">{t('onboarding.yml_pr_heading')}</h3>
        {/* Prose only — the card's ONE link to Repos is the footer's, where both
            yml states put it. Two identical CTAs on one card is clutter, and the
            action band is where a reader already looks for the way out. */}
        <p>{t('onboarding.yml_pr_lead')}</p>
      </section>

      {/* The two fallbacks side by side once the panel is wide enough — the
          setup-PR step is one of the two that get the room. */}
      <div class="onboarding__yml-pair">
        <section class="onboarding__yml">
          <h3 class="onboarding__yml-heading">{t('onboarding.yml_minimal_heading')}</h3>
          <p>{t('onboarding.yml_minimal_body')}</p>
          <pre class="code-block">{t('onboarding.yml_minimal_file')}</pre>
          <OnboardingAdvanceButton
            label={t('onboarding.yml_minimal_copy')}
            busy={props.busy}
            secondary
            onAct={() => props.onCopy(t('onboarding.yml_minimal_file'))}
          />
        </section>

        <section class="onboarding__yml">
          <h3 class="onboarding__yml-heading">{t('onboarding.yml_tailor_heading')}</h3>
          <p>{t('onboarding.yml_tailor_body')}</p>
          <pre class="code-block">{t('onboarding.yml_prompt')}</pre>
          {/* The prompt's own copy button. It used to live in the card FOOTER, as
            the step's primary action — which is the seat the pull request now
            takes. A 1.5 KB block a user is told to copy needs a button beside it,
            not a hand selection. */}
          <OnboardingAdvanceButton
            label={t('onboarding.yml_copy_button')}
            busy={props.busy}
            secondary
            onAct={() => props.onCopy(t('onboarding.yml_prompt'))}
          />
          <a
            class={buttonClass({ variant: 'ghost', size: 'sm' })}
            href="https://developerz.ai/docs/maintainer-yml"
            target="_blank"
            rel="noreferrer"
          >
            {t('onboarding.yml_docs_cta')}
          </a>
        </section>
      </div>
    </>
  );
}
